import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { todayInTimezone } from '@/utils/dateUtils';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  AppWindow,
  Globe,
  BarChart3,
  Clock,
  Shield,
  ChevronRight,
  Search,
  RefreshCw,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  Upload,
  FileSpreadsheet,
  Download,
  TrendingUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useLanguage } from '@/i18n/LanguageContext';

function UsagePolicyPage() {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [staffWithPolicies, setStaffWithPolicies] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [topApps, setTopApps] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartTotals, setChartTotals] = useState(null);
  
  // Dialog states
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showAppDialog, setShowAppDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // Import states
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Form states
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyName, setPolicyName] = useState('');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [appName, setAppName] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [appAction, setAppAction] = useState('allow');
  const [editingApp, setEditingApp] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [selectedDate, setSelectedDate] = useState('');  // set by backend on first load
  const initialDateSet = useRef(false);

  useEffect(() => {
    const loadCompanyTimezone = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings/company`, { withCredentials: true });
        setCompanyTimezone(res.data?.timezone || 'UTC');
      } catch (e) {
        // ignore
      }
    };
    loadCompanyTimezone();
  }, []);

  // Fetch chart data for last 7 days
  const fetchChartData = async () => {
    try {
      const res = await axios.get(`${API}/admin/usage-reports/chart-data?days=7`, { withCredentials: true });
      setChartData(res.data.chart_data || []);
      setChartTotals(res.data.totals || null);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchData = async (dateOverride) => {
    setLoading(true);
    try {
      // On first load: omit date so backend uses company timezone to compute correct date
      const dateParam = dateOverride || (initialDateSet.current ? selectedDate : '');
      const dateQuery = dateParam ? `?date=${dateParam}` : '';

      const [policiesRes, staffRes, summaryRes, topAppsRes] = await Promise.all([
        axios.get(`${API}/admin/usage-policies`, { withCredentials: true }),
        axios.get(`${API}/admin/staff-policies`, { withCredentials: true }),
        axios.get(`${API}/admin/usage-reports/summary${dateQuery}`, { withCredentials: true }),
        axios.get(`${API}/admin/usage-reports/top-apps${dateQuery}`, { withCredentials: true }),
      ]);

      // On first load: use the backend-returned date as the source of truth
      if (!initialDateSet.current) {
        initialDateSet.current = true;
        const backendDate = summaryRes.data?.date;
        if (backendDate) {
          setSelectedDate(backendDate);
          setCompanyTimezone(summaryRes.data?.timezone || 'UTC');
        }
      }

      setPolicies(policiesRes.data);
      setStaffWithPolicies(staffRes.data);
      setUsageSummary(summaryRes.data);
      setTopApps(topAppsRes.data.top_apps || []);
      await fetchChartData();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Initial load — no date param so backend computes correct company-timezone date
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when user picks a different date
  useEffect(() => {
    if (initialDateSet.current && selectedDate) {
      fetchData(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Policy CRUD
  const handleSavePolicy = async () => {
    if (!policyName.trim()) {
      toast.error('Please enter a policy name');
      return;
    }
    try {
      if (editingPolicy) {
        await axios.put(`${API}/admin/usage-policies/${editingPolicy.policy_id}`, {
          category_name: policyName.trim(),
        }, { withCredentials: true });
        toast.success('Policy updated');
      } else {
        await axios.post(`${API}/admin/usage-policies`, {
          category_name: policyName.trim(),
        }, { withCredentials: true });
        toast.success('Policy created');
      }
      setShowPolicyDialog(false);
      setPolicyName('');
      setEditingPolicy(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };

  const handleDeletePolicy = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/usage-policies/${deleteTarget.policy_id}`, { withCredentials: true });
      toast.success('Policy deleted');
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete policy');
    }
  };

  const openEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyName(policy.category_name);
    setShowPolicyDialog(true);
  };

  // App CRUD
  const [addRuleType, setAddRuleType] = useState('application'); // 'url' or 'application'
  const handleSaveApp = async () => {
    try {
      const payload = { app_action: appAction, rule_type: addRuleType };
      if (addRuleType === 'url') {
        if (!siteUrl.trim()) { toast.error('Please enter a website URL'); return; }
        payload.site_url = siteUrl.trim();
        payload.app_name = appName.trim() || siteUrl.trim();
      } else {
        if (!appName.trim()) { toast.error('Please enter an application name'); return; }
        payload.app_name = appName.trim();
      }
      if (editingApp) {
        await axios.put(
          `${API}/admin/usage-policies/${selectedPolicy.policy_id}/apps/${editingApp.app_id}`,
          payload, { withCredentials: true }
        );
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/admin/usage-policies/${selectedPolicy.policy_id}/apps`, payload, { withCredentials: true });
        toast.success('Rule added');
      }
      setShowAppDialog(false);
      setAppName(''); setSiteUrl(''); setAppAction('allow'); setEditingApp(null);
      const res = await axios.get(`${API}/admin/usage-policies/${selectedPolicy.policy_id}`, { withCredentials: true });
      setSelectedPolicy(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDeleteApp = async (appId) => {
    try {
      await axios.delete(`${API}/admin/usage-policies/${selectedPolicy.policy_id}/apps/${appId}`, { withCredentials: true });
      toast.success('App removed');
      const res = await axios.get(`${API}/admin/usage-policies/${selectedPolicy.policy_id}`, { withCredentials: true });
      setSelectedPolicy(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove app');
    }
  };

  // Staff Policy Assignment
  const handleAssignPolicy = async (staffId, policyId) => {
    try {
      await axios.post(`${API}/admin/usage-policies/assign`, {
        staff_id: staffId,
        policy_id: policyId || null,
      }, { withCredentials: true });
      toast.success(policyId ? 'Policy assigned' : 'Policy unassigned');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign policy');
    }
  };

  // Staff Detail View
  const handleViewStaffDetail = (staff) => {
    // Use backend-returned date from summary (always correct company timezone)
    const dateToUse = usageSummary?.date || selectedDate;
    navigate(`/admin/usage-policy/staff/${staff.staff_id}?date=${dateToUse}`);
  };

  // Import Apps from CSV/Excel
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseFileContent(text, file.name);
    };
    reader.readAsText(file);
  };

  const parseFileContent = (text, filename) => {
    const lines = text.split('\n').filter(line => line.trim());
    const apps = [];
    
    // Skip header row if it looks like a header
    const startIndex = lines[0]?.toLowerCase().includes('app') || 
                       lines[0]?.toLowerCase().includes('name') ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Handle both CSV and simple text format
      let parts;
      if (line.includes(',')) {
        // CSV format: app_name,site_url
        parts = line.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
      } else if (line.includes('\t')) {
        // Tab-separated
        parts = line.split('\t').map(p => p.trim());
      } else {
        // Single column - just app name
        parts = [line];
      }
      
      const appName = parts[0]?.trim();
      const siteUrl = parts[1]?.trim() || '';
      
      if (appName) {
        apps.push({ app_name: appName, site_url: siteUrl || null });
      }
    }
    
    setImportPreview(apps);
  };

  const handleImportApps = async () => {
    if (!importPreview.length || !selectedPolicy) return;
    
    setImporting(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const app of importPreview) {
      try {
        await axios.post(`${API}/admin/usage-policies/${selectedPolicy.policy_id}/apps`, {
          app_name: app.app_name,
          site_url: app.site_url,
        }, { withCredentials: true });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to import ${app.app_name}:`, error);
      }
    }
    
    setImporting(false);
    setShowImportDialog(false);
    setImportFile(null);
    setImportPreview([]);
    
    // Refresh the selected policy
    const res = await axios.get(`${API}/admin/usage-policies/${selectedPolicy.policy_id}`, { withCredentials: true });
    setSelectedPolicy(res.data);
    fetchData();
    
    if (errorCount > 0) {
      toast.warning(`Imported ${successCount} apps. ${errorCount} failed (possibly duplicates).`);
    } else {
      toast.success(`Successfully imported ${successCount} apps!`);
    }
  };

  const downloadTemplate = () => {
    const template = 'App Name,Website URL\nMicrosoft Word,\nGoogle Chrome,https://google.com\nSlack,https://slack.com\nVS Code,\nZoom,https://zoom.us';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'app_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredStaff = staffWithPolicies.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Policy</h1>
          <p className="text-gray-500">Track and manage application usage for staff</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40 border-0 p-0 h-auto focus-visible:ring-0"
            />
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { setEditingPolicy(null); setPolicyName(''); setShowPolicyDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Create Policy
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Policies</p>
                <p className="text-2xl font-bold">{policies.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Staff Tracked</p>
                <p className="text-2xl font-bold">{usageSummary?.staff_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Clock className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Usage Today</p>
                <p className="text-2xl font-bold">
                  {usageSummary?.staff_usage?.reduce((sum, s) => sum + s.total_duration_seconds, 0) > 0
                    ? formatDuration(usageSummary.staff_usage.reduce((sum, s) => sum + s.total_duration_seconds, 0))
                    : '0m'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <AppWindow className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unique Apps</p>
                <p className="text-2xl font-bold">{topApps.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="policies" className="space-y-4">
        <TabsList>
          <TabsTrigger value="policies" data-testid="tab-policies">
            <Shield className="w-4 h-4 mr-2" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">
            <Users className="w-4 h-4 mr-2" />
            Staff Assignment
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <BarChart3 className="w-4 h-4 mr-2" />
            Usage Reports
          </TabsTrigger>
        </TabsList>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-4">
          {policies.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No policies yet</h3>
                <p className="text-gray-500 mb-4">Create your first usage policy to start tracking</p>
                <Button onClick={() => setShowPolicyDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Policy
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {policies.map((policy) => (
                <Card key={policy.policy_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{policy.category_name}</CardTitle>
                        <CardDescription>Created {new Date(policy.created_at).toLocaleDateString()}</CardDescription>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditPolicy(policy)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => { setDeleteTarget(policy); setShowDeleteConfirm(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <AppWindow className="w-4 h-4" />
                        <span>{policy.apps_count} apps</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{policy.staff_count} staff</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={async () => {
                        const res = await axios.get(`${API}/admin/usage-policies/${policy.policy_id}`, { withCredentials: true });
                        setSelectedPolicy(res.data);
                      }}
                    >
                      Manage Apps
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Selected Policy Apps View */}
          {selectedPolicy && (
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      {selectedPolicy.category_name} - Rules
                    </CardTitle>
                    <CardDescription>
                      {selectedPolicy.apps?.length || 0} rules in this policy
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => { setEditingApp(null); setAddRuleType('url'); setAppName(''); setSiteUrl(''); setAppAction('block'); setShowAppDialog(true); }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Add Website/URL
                    </Button>
                    <Button
                      onClick={() => { setEditingApp(null); setAddRuleType('application'); setAppName(''); setSiteUrl(''); setAppAction('block'); setShowAppDialog(true); }}
                    >
                      <AppWindow className="w-4 h-4 mr-2" />
                      Add Application
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setImportFile(null); setImportPreview([]); setShowImportDialog(true); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import
                    </Button>
                    <Button variant="outline" onClick={() => setSelectedPolicy(null)}>
                      Close
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* URL / Website Rules */}
                {(() => {
                  const urlRules = (selectedPolicy.apps || []).filter(a => a.rule_type === 'url' || (a.site_url && !a.rule_type));
                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 rounded-lg bg-blue-100">
                          <Globe className="w-4 h-4 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-800">Website & URL Rules</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{urlRules.length}</span>
                        <span className="text-xs text-gray-500 ml-1">— applies regardless of which browser is used</span>
                      </div>
                      {urlRules.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 border-2 border-dashed rounded-xl text-sm">
                          No website rules yet. Click <strong>Add Website/URL</strong> to block or allow a domain across all browsers.
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Website / Domain</TableHead>
                              <TableHead>Label (optional)</TableHead>
                              <TableHead>Action</TableHead>
                              <TableHead>Added</TableHead>
                              <TableHead className="text-right">{t("common.actions")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {urlRules.map((app) => (
                              <TableRow key={app.app_id}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2 text-blue-600">
                                    <Globe className="w-4 h-4 flex-shrink-0" />
                                    <a href={app.site_url?.startsWith('http') ? app.site_url : `https://${app.site_url}`} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[200px]">
                                      {app.site_url}
                                    </a>
                                  </div>
                                </TableCell>
                                <TableCell className="text-gray-500 text-sm">{(app.app_name !== app.site_url) ? app.app_name : <span className="text-gray-300">—</span>}</TableCell>
                                <TableCell>
                                  <Select value={app.app_action || 'allow'} onValueChange={async (val) => {
                                    try {
                                      await axios.put(`${API}/admin/usage-policies/${selectedPolicy.policy_id}/apps/${app.app_id}`, { app_action: val }, { withCredentials: true });
                                      toast.success('Action updated');
                                      const res = await axios.get(`${API}/admin/usage-policies/${selectedPolicy.policy_id}`, { withCredentials: true });
                                      setSelectedPolicy(res.data); fetchData();
                                    } catch (e) { toast.error('Failed to update action'); }
                                  }}>
                                    <SelectTrigger className={`w-[100px] ${app.app_action === 'block' ? 'border-red-200 text-red-700' : 'border-indigo-200 text-indigo-700'}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="allow">Allow</SelectItem>
                                      <SelectItem value="block">Block</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell className="text-gray-500">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => { setEditingApp(app); setAddRuleType('url'); setAppName(app.app_name || ''); setSiteUrl(app.site_url || ''); setAppAction(app.app_action || 'allow'); setShowAppDialog(true); }}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteApp(app.app_id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  );
                })()}

                {/* Application Rules */}
                {(() => {
                  const appRules = (selectedPolicy.apps || []).filter(a => a.rule_type === 'application' || (!a.site_url && !a.rule_type));
                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 rounded-lg bg-purple-100">
                          <AppWindow className="w-4 h-4 text-purple-600" />
                        </div>
                        <h3 className="font-semibold text-gray-800">Desktop Application Rules</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{appRules.length}</span>
                        <span className="text-xs text-gray-500 ml-1">— matches by process/application name</span>
                      </div>
                      {appRules.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 border-2 border-dashed rounded-xl text-sm">
                          No application rules yet. Click <strong>Add Application</strong> to block or allow a desktop app like TikTok, Slack, VS Code.
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Application Name</TableHead>
                              <TableHead>Action</TableHead>
                              <TableHead>Added</TableHead>
                              <TableHead className="text-right">{t("common.actions")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {appRules.map((app) => (
                              <TableRow key={app.app_id}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <AppWindow className="w-4 h-4 text-gray-400" />
                                    {app.app_name}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Select value={app.app_action || 'allow'} onValueChange={async (val) => {
                                    try {
                                      await axios.put(`${API}/admin/usage-policies/${selectedPolicy.policy_id}/apps/${app.app_id}`, { app_action: val }, { withCredentials: true });
                                      toast.success('Action updated');
                                      const res = await axios.get(`${API}/admin/usage-policies/${selectedPolicy.policy_id}`, { withCredentials: true });
                                      setSelectedPolicy(res.data); fetchData();
                                    } catch (e) { toast.error('Failed to update action'); }
                                  }}>
                                    <SelectTrigger className={`w-[100px] ${app.app_action === 'block' ? 'border-red-200 text-red-700' : 'border-indigo-200 text-indigo-700'}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="allow">Allow</SelectItem>
                                      <SelectItem value="block">Block</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell className="text-gray-500">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => { setEditingApp(app); setAddRuleType('application'); setAppName(app.app_name || ''); setSiteUrl(''); setAppAction(app.app_action || 'allow'); setShowAppDialog(true); }}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteApp(app.app_id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Staff Assignment Tab */}
        <TabsContent value="staff" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Staff Policy Assignment</CardTitle>
                  <CardDescription>Assign usage policies to staff members</CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search staff..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Name</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("staff.department")}</TableHead>
                    <TableHead>Assigned Policy</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStaff.map((staff) => (
                    <TableRow key={staff.staff_id}>
                      <TableCell className="font-medium">{staff.name}</TableCell>
                      <TableCell className="text-gray-500">{staff.email}</TableCell>
                      <TableCell>{staff.department || '-'}</TableCell>
                      <TableCell>
                        <Select
                          value={staff.group_policy_id || 'none'}
                          onValueChange={(value) => handleAssignPolicy(staff.staff_id, value === 'none' ? null : value)}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Select policy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Policy</SelectItem>
                            {policies.map((p) => (
                              <SelectItem key={p.policy_id} value={p.policy_id}>
                                {p.category_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {staff.usage_policy_enabled ? (
                          <Badge className="bg-indigo-100 text-indigo-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Tracking
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            <XCircle className="w-3 h-3 mr-1" />
                            Not Tracking
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewStaffDetail(staff)}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          {/* 7-Day Usage Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Last 7 Days Usage Overview
                  </CardTitle>
                  <CardDescription>
                    App and website usage categorized by policy status (in minutes)
                  </CardDescription>
                </div>
                {chartTotals && (
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-indigo-500"></div>
                      <span>Allowed: {chartTotals.allowed_hours}h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-red-500"></div>
                      <span>Blocked: {chartTotals.blocked_hours}h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-orange-500"></div>
                      <span>Unlisted: {chartTotals.unlisted_hours}h</span>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {chartData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No usage data available for the last 7 days</p>
                </div>
              ) : (
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis 
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#6b7280' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value, name) => {
                          const label = name === 'allowed' ? 'Allowed' : name === 'blocked' ? 'Blocked' : 'Unlisted';
                          return [`${value} min`, label];
                        }}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            return payload[0].payload.date;
                          }
                          return label;
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        formatter={(value) => {
                          return value === 'allowed' ? 'Allowed Apps' : value === 'blocked' ? 'Blocked Apps' : 'Unlisted Apps';
                        }}
                      />
                      <Bar dataKey="allowed" name="allowed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="blocked" name="blocked" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="unlisted" name="unlisted" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff Usage Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Staff Usage Summary - {selectedDate}</CardTitle>
              <CardDescription>Application and website usage by staff members</CardDescription>
            </CardHeader>
            <CardContent>
              {!usageSummary?.staff_usage || usageSummary.staff_usage.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No active staff members found</p>
                  <p className="text-sm mt-1">Add staff members to track their usage</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Name</TableHead>
                      <TableHead>Policy</TableHead>
                      <TableHead>Total Time</TableHead>
                      <TableHead>Tracked</TableHead>
                      <TableHead>Untracked</TableHead>
                      <TableHead>Apps</TableHead>
                      <TableHead className="text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usageSummary?.staff_usage?.map((staff) => (
                      <TableRow key={staff.staff_id}>
                        <TableCell className="font-medium">
                          <div>
                            <p>{staff.name}</p>
                            <p className="text-xs text-gray-500">{staff.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {staff.policy_enabled ? (
                            <Badge className="bg-indigo-100 text-indigo-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : staff.has_policy ? (
                            <Badge className="bg-yellow-100 text-yellow-700">
                              Assigned
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500">
                              None
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-700">
                            {staff.total_duration_formatted || '0s'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-indigo-100 text-indigo-700">
                            {staff.tracked_duration_formatted || '0s'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-orange-100 text-orange-700">
                            {staff.untracked_duration_formatted || '0s'}
                          </Badge>
                        </TableCell>
                        <TableCell>{staff.unique_apps || 0}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewStaffDetail(staff)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Top Apps */}
          <Card>
            <CardHeader>
              <CardTitle>Top Applications - {selectedDate}</CardTitle>
              <CardDescription>Most used apps and websites across all tracked staff</CardDescription>
            </CardHeader>
            <CardContent>
              {topApps.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No app usage data for this date
                </div>
              ) : (
                <div className="space-y-3">
                  {topApps.slice(0, 10).map((app, index) => (
                    <div key={app.app_name} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{app.app_name}</span>
                          {app.is_tracked && (
                            <Badge variant="outline" className="text-indigo-600 border-indigo-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Tracked
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {app.staff_count} staff · {app.total_formatted}
                        </div>
                      </div>
                      <div className="w-32">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                            style={{
                              width: `${Math.min(100, (app.total_seconds / (topApps[0]?.total_seconds || 1)) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Policy Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create New Policy'}</DialogTitle>
            <DialogDescription>
              {editingPolicy
                ? 'Update the policy name'
                : 'Create a new usage policy group to track applications and websites'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="policy-name">Policy Name</Label>
              <Input
                id="policy-name"
                placeholder="e.g., Productivity Apps, Social Media, Development Tools"
                value={policyName}
                onChange={(e) => setPolicyName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePolicy}>
              {editingPolicy ? 'Update' : 'Create'} Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* App Dialog */}
      <Dialog open={showAppDialog} onOpenChange={setShowAppDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {addRuleType === 'url'
                ? <><Globe className="w-5 h-5 text-blue-600" />{editingApp ? 'Edit Website Rule' : 'Add Website / URL Rule'}</>
                : <><AppWindow className="w-5 h-5 text-purple-600" />{editingApp ? 'Edit Application Rule' : 'Add Application Rule'}</>
              }
            </DialogTitle>
            <DialogDescription>
              {addRuleType === 'url'
                ? 'Block or allow a website domain across ALL browsers — regardless of which app is used to open it.'
                : 'Block or allow a desktop application by its process name (e.g., TikTok, Slack, Zoom).'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {addRuleType === 'url' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="site-url">Website Domain / URL <span className="text-red-500">*</span></Label>
                  <Input
                    id="site-url"
                    placeholder="e.g., youtube.com or facebook.com"
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500">Enter the domain (e.g., <code>youtube.com</code>). This rule will apply in Chrome, Firefox, Edge and any other browser.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app-name">Label (optional)</Label>
                  <Input
                    id="app-name"
                    placeholder="e.g., YouTube (leave blank to use domain as label)"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="app-name">Application Name <span className="text-red-500">*</span></Label>
                <Input
                  id="app-name"
                  placeholder="e.g., TikTok, Slack, Zoom, VS Code"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  autoFocus
                />
                <p className="text-xs text-gray-500">Enter the exact application process name as it appears in the activity tracker (e.g., <code>TikTok</code>, <code>GitHubDesktop</code>).</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Action</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setAppAction('allow')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all ${appAction === 'allow' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                >
                  <CheckCircle className="w-4 h-4" /> Allow
                </button>
                <button
                  type="button"
                  onClick={() => setAppAction('block')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all ${appAction === 'block' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                >
                  <XCircle className="w-4 h-4" /> Block
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveApp} className={addRuleType === 'url' ? 'bg-blue-600 hover:bg-blue-700' : ''}>
              {editingApp ? 'Update Rule' : `Add ${addRuleType === 'url' ? 'Website Rule' : 'Application Rule'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete &quot;{deleteTarget?.category_name}&quot; and unassign it from all staff members.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePolicy} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Apps Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              Import Apps to {selectedPolicy?.category_name}
            </DialogTitle>
            <DialogDescription>
              Upload a CSV or Excel file with app names and optional website URLs
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Template Download */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-700">Download Template</p>
                <p className="text-sm text-gray-500">Get a sample CSV file to fill in</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Upload File</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="import-file-input"
                />
                <label htmlFor="import-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {importFile ? importFile.name : 'Click to upload CSV or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Supports CSV, TXT, Excel files</p>
                </label>
              </div>
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Preview ({importPreview.length} apps found)</Label>
                  <Badge className="bg-indigo-100 text-indigo-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ready to import
                  </Badge>
                </div>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>App Name</TableHead>
                        <TableHead>Website URL</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.slice(0, 10).map((app, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-gray-400">{index + 1}</TableCell>
                          <TableCell className="font-medium">{app.app_name}</TableCell>
                          <TableCell className="text-gray-500">{app.site_url || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {importPreview.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-gray-500">
                            ... and {importPreview.length - 10} more apps
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Format Help */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              <p className="font-medium mb-1">Supported formats:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>CSV: <code className="bg-gray-200 px-1 rounded">App Name,Website URL</code></li>
                <li>One app per line (URL optional)</li>
                <li>Header row is auto-detected and skipped</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImportApps} 
              disabled={importPreview.length === 0 || importing}
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import {importPreview.length} Apps
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default UsagePolicyPage;
